import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usuariosService, Usuario } from '../services/api/usuariosService';
import { authService } from '../services/authService';
import { useOraclePhoto } from '../hooks/useOraclePhoto';

// Componente auxiliar para renderizar foto com hook
const UsuarioFoto: React.FC<{ 
  usuario: Usuario;
}> = ({ usuario }) => {
  // Hook Oracle para buscar URL assinada
  const { url: oracleUrl, loading } = useOraclePhoto(
    usuario.id ? parseInt(usuario.id) : undefined,
    !!usuario.foto_perfil_storage_key,
    '/img/avatar.svg'
  );

  // Se não tem storage_key, mostrar avatar padrão ou ícone
  if (!usuario.foto_perfil_storage_key) {
    return (
      <div className="img-circle elevation-1 bg-secondary d-flex align-items-center justify-content-center"
           style={{ width: '32px', height: '32px' }}>
        <i className="fas fa-user text-white"></i>
      </div>
    );
  }

  // Se está carregando, mostrar placeholder
  if (loading) {
    return (
      <div className="img-circle elevation-1 bg-light d-flex align-items-center justify-content-center"
           style={{ width: '32px', height: '32px' }}>
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="sr-only">Carregando...</span>
        </div>
      </div>
    );
  }

  // ✅ Renderizar imagem (fallback já tratado pelo hook)
  return (
    <img 
      src={oracleUrl || '/img/avatar.svg'}
      alt={usuario.nome}
      className="img-circle elevation-1"
      style={{ width: '32px', height: '32px', objectFit: 'cover' }}
      onError={(e) => {
        // ✅ CORREÇÃO: Apenas trocar imagem, SEM invalidar cache (previne loop infinito)
        const imgElement = e.target as HTMLImageElement;
        imgElement.src = '/img/avatar.svg';
        imgElement.onerror = null; // Prevenir loops
      }}
    />
  );
};

const ListaUsuarios: React.FC = () => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [senhaResetada, setSenhaResetada] = useState<{ senha: string, userId: string } | null>(null);
  // Responsividade mobile
  const [isMobile, setIsMobile] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  
  const usuarioAtual = authService.getUsuario();
  
  // ✅ Recarregar lista ao montar e quando página voltar ao foco
  useEffect(() => {
    carregarUsuarios();
    
    // Recarregar quando página voltar ao foco (após editar usuário)
    const handleVisibilityChange = () => {
      if (!document.hidden) {

        carregarUsuarios();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
  
  const carregarUsuarios = async () => {
    try {
      setCarregando(true);
      setErro(null);

      const data = await usuariosService.listarUsuarios();

      setUsuarios(data);
    } catch (error: any) {
      console.error('Detalhes do erro ao carregar usuários:', error);
      
      // Verificar se é erro de cancelamento e não mostrar para o usuário
      if (error.isRequestCancellation) {

      } else {
        setErro(`Erro ao carregar usuários: ${error.message || 'Falha na conexão'}`);
      }
    } finally {
      setCarregando(false);
    }
  };
  
  const alterarStatus = async (id: string, novoStatus: boolean) => {
    try {
      await usuariosService.alterarStatus(id, novoStatus);
      setUsuarios(usuarios.map(u => u.id === id ? { ...u, ativo: novoStatus } : u));
    } catch (error: any) {
      const mensagem = error?.friendlyMessage || 'Não foi possível alterar o status do usuário.';
      alert(mensagem);
    }
  };
  
  const resetarSenha = async (id: string, nome: string) => {
    if (window.confirm(`Tem certeza que deseja resetar a senha de ${nome}?`)) {
      try {
        const resultado = await usuariosService.resetarSenha(id);
        setSenhaResetada({ senha: resultado.senha, userId: id });
      } catch (error) {
        setErro('Erro ao resetar senha');
        console.error(error);
      }
    }
  };

  const excluirUsuario = async (id: string, nome: string) => {
    if (!usuarioAtual || usuarioAtual.cargo !== 'admin') return;
    if (!window.confirm(`Excluir definitivamente o usuário "${nome}"? Esta ação não pode ser desfeita.`)) {
      return;
    }
    try {
      await usuariosService.excluirUsuario(id);
      setUsuarios(prev => prev.filter(u => u.id !== id));
    } catch (error: any) {
      console.error('[ListaUsuarios] Erro ao excluir usuário:', error);
      if (error?.response?.status === 409) {
        const deps = error.response.data?.detalhes;
        const msg = error.response.data?.mensagem || 'Usuário possui dependências.';
        const detalheStr = deps ? `\nPacientes: ${deps.pacientes}\nConsultas: ${deps.consultas}\nProntuários: ${deps.prontuarios}` : '';
        alert(msg + detalheStr + '\n\nTransfira os vínculos para outro profissional antes de excluir.');
      } else {
        alert('Erro ao excluir usuário.');
      }
    }
  };
  
  return (
    <div className="container-fluid">
      {erro && (
        <div className="alert alert-danger" role="alert">
          {erro}
          <button 
            type="button" 
            className="close" 
            onClick={() => setErro(null)}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}
      
      {senhaResetada && (
        <div className="alert alert-success alert-dismissible">
          <h5><i className="icon fas fa-check"></i> Senha resetada com sucesso!</h5>
          <p>A nova senha para o usuário é: <strong>{senhaResetada.senha}</strong></p>
          <p className="text-muted">Anote esta senha e informe ao usuário. Por segurança, esta informação não será exibida novamente.</p>
          <button 
            type="button" 
            className="close" 
            onClick={() => setSenhaResetada(null)}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}
      
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            <i className="fas fa-users mr-1"></i>
            Lista de Usuários
          </h3>
          <div className="card-tools">
            {/* Ocultar criação de novo usuário para empresas tipo estudante */}
            {(() => {
              try {
                const rawEmpresa = sessionStorage.getItem('empresa');
                const tipoEmpresa = rawEmpresa ? JSON.parse(rawEmpresa)?.tipo_empresa : undefined;
                const isEstudanteEmpresa = tipoEmpresa === 'estudante';
                if (!isEstudanteEmpresa && usuarioAtual?.cargo === 'admin') {
                  return (
                    <Link to="/usuarios/novo" className="btn btn-primary btn-sm">
                      <i className="fas fa-plus"></i> Novo Usuário
                    </Link>
                  );
                }
                if (isEstudanteEmpresa) {
                  return (
                    <span className="text-muted small">
                      <i className="fas fa-lock mr-1"></i>
                      Plano estudante: criação de novos usuários bloqueada
                    </span>
                  );
                }
              } catch (e) {
                // Falha silenciosa
              }
              return null;
            })()}
          </div>
        </div>
        
        <div className="card-body p-0">
          {carregando ? (
            <div className="d-flex justify-content-center p-5">
              <div className="spinner-border" role="status">
                <span className="sr-only">Carregando...</span>
              </div>
            </div>
          ) : (
            isMobile ? (
              <div className="usuarios-mobile-list">
                {usuarios.length === 0 ? (
                  <div className="p-4 text-center text-muted">
                    Nenhum usuário encontrado
                  </div>
                ) : (
                  usuarios.map(usuario => {
                    let tipoEmpresa: string | undefined = undefined;
                    try {
                      const rawEmpresa = sessionStorage.getItem('empresa');
                      tipoEmpresa = rawEmpresa ? JSON.parse(rawEmpresa)?.tipo_empresa : undefined;
                    } catch {}
                    const isEstudanteEmpresa = tipoEmpresa === 'estudante';
                    const isMeuUsuario = usuario.id === usuarioAtual?.id;
                    return (
                      <div key={usuario.id} className={`usuario-mobile-card ${!usuario.ativo ? 'inativo' : ''}`}>
                        <div className="usuario-header">
                          <div className="d-flex align-items-center gap-2">
                            <UsuarioFoto usuario={usuario} />
                            <div>
                              <h4 className="usuario-nome mb-0">{usuario.nome}</h4>
                              <small className="text-muted">@{usuario.usuario}</small>
                            </div>
                          </div>
                          <span className={`badge badge-${usuario.ativo ? 'success' : 'danger'}`}>{usuario.ativo ? 'Ativo' : 'Inativo'}</span>
                        </div>
                        <div className="usuario-meta mt-2">
                          <div><i className="fas fa-envelope"></i> {usuario.email}</div>
                          <div>
                            {usuario.cargo === 'admin' && <span className="badge badge-danger">Administrador</span>}
                            {usuario.cargo === 'psicologo' && <span className="badge badge-primary">Psicólogo</span>}
                            {usuario.cargo === 'estudante' && <span className="badge badge-warning">Estudante</span>}
                            {usuario.cargo === 'atendente' && <span className="badge badge-info">Atendente</span>}
                          </div>
                          <div><strong>CRP:</strong> {usuario.crp || '-'}</div>
                          <div><strong>Último login:</strong> {usuario.ultimo_login ? new Date(usuario.ultimo_login).toLocaleDateString() : '-'}</div>
                        </div>
                        <div className="usuario-actions mt-3">
                          {isEstudanteEmpresa ? (
                            <>
                              <Link
                                to={`/usuarios/${usuario.id}/editar`}
                                className={`btn btn-sm btn-info ${!isMeuUsuario ? 'disabled' : ''}`}
                                title={isMeuUsuario ? 'Editar' : 'Plano estudante: só é possível editar seu próprio usuário'}
                                onClick={(e) => { if (!isMeuUsuario) e.preventDefault(); }}
                              >
                                <i className="fas fa-edit"></i>
                              </Link>
                              <button type="button" className="btn btn-sm btn-secondary disabled" title="Alterar senha disponível no menu do usuário (canto superior)">
                                <i className="fas fa-key"></i>
                              </button>
                            </>
                          ) : (
                            <>
                              <Link 
                                to={`/usuarios/${usuario.id}/editar`} 
                                className="btn btn-sm btn-info" 
                                title="Editar"
                              >
                                <i className="fas fa-edit"></i>
                              </Link>
                              {usuario.id !== usuarioAtual?.id && (
                                <button 
                                  type="button" 
                                  className={`btn btn-sm btn-${usuario.ativo ? 'warning' : 'success'}`}
                                  onClick={() => alterarStatus(usuario.id!, !usuario.ativo)}
                                  title={usuario.ativo ? 'Desativar' : 'Ativar'}
                                >
                                  <i className={`fas fa-${usuario.ativo ? 'ban' : 'check'}`}></i>
                                </button>
                              )}
                              <button 
                                type="button" 
                                className="btn btn-sm btn-secondary"
                                onClick={() => resetarSenha(usuario.id!, usuario.nome)}
                                title="Resetar senha"
                              >
                                <i className="fas fa-key"></i>
                              </button>
                              {usuarioAtual && usuarioAtual.cargo === 'admin' && !!usuario.id && !!usuarioAtual.id && usuario.id !== String(usuarioAtual.id) && (
                                <button 
                                  type="button" 
                                  className="btn btn-sm btn-danger"
                                  onClick={() => excluirUsuario(usuario.id!, usuario.nome)}
                                  title="Excluir usuário"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}></th>
                    <th>Nome</th>
                    <th>Usuário</th>
                    <th>E-mail</th>
                    <th>Cargo</th>
                    <th>CRP</th>
                    <th>Último Login</th>
                    <th>Status</th>
                    <th style={{ width: '150px' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center">Nenhum usuário encontrado</td>
                    </tr>
                  ) : (
                    usuarios.map(usuario => (
                      <tr key={usuario.id}>
                        <td>
                          <UsuarioFoto usuario={usuario} />
                        </td>
                        <td>{usuario.nome}</td>
                        <td>{usuario.usuario}</td>
                        <td>{usuario.email}</td>
                        <td>
                          {usuario.cargo === 'admin' && <span className="badge badge-danger">Administrador</span>}
                          {usuario.cargo === 'psicologo' && <span className="badge badge-primary">Psicólogo</span>}
                          {usuario.cargo === 'estudante' && <span className="badge badge-warning">Estudante</span>}
                          {usuario.cargo === 'atendente' && <span className="badge badge-info">Atendente</span>}
                        </td>
                        <td>{usuario.crp || '-'}</td>
                        <td>{usuario.ultimo_login ? new Date(usuario.ultimo_login).toLocaleString() : '-'}</td>
                        <td>
                          <span className={`badge badge-${usuario.ativo ? 'success' : 'danger'}`}>
                            {usuario.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td>
                          {(() => {
                            let tipoEmpresa: string | undefined = undefined;
                            try {
                              const rawEmpresa = sessionStorage.getItem('empresa');
                              tipoEmpresa = rawEmpresa ? JSON.parse(rawEmpresa)?.tipo_empresa : undefined;
                            } catch {}
                            const isEstudanteEmpresa = tipoEmpresa === 'estudante';
                            const isMeuUsuario = usuario.id === usuarioAtual?.id;
                            if (isEstudanteEmpresa) {
                              return (
                                <div className="btn-group">
                                  <Link
                                    to={`/usuarios/${usuario.id}/editar`}
                                    className={`btn btn-sm btn-info ${!isMeuUsuario ? 'disabled' : ''}`}
                                    title={isMeuUsuario ? 'Editar' : 'Plano estudante: só é possível editar seu próprio usuário'}
                                    onClick={(e) => { if (!isMeuUsuario) e.preventDefault(); }}
                                  >
                                    <i className="fas fa-edit"></i>
                                  </Link>
                                  <button type="button" className="btn btn-sm btn-secondary disabled" title="Alterar senha disponível no menu do usuário (canto superior)">
                                    <i className="fas fa-key"></i>
                                  </button>
                                </div>
                              );
                            }
                            return (
                              <div className="btn-group">
                                <Link 
                                  to={`/usuarios/${usuario.id}/editar`} 
                                  className="btn btn-sm btn-info" 
                                  title="Editar"
                                >
                                  <i className="fas fa-edit"></i>
                                </Link>
                                {usuario.id !== usuarioAtual?.id && (
                                  <button 
                                    type="button" 
                                    className={`btn btn-sm btn-${usuario.ativo ? 'warning' : 'success'}`}
                                    onClick={() => alterarStatus(usuario.id!, !usuario.ativo)}
                                    title={usuario.ativo ? 'Desativar' : 'Ativar'}
                                  >
                                    <i className={`fas fa-${usuario.ativo ? 'ban' : 'check'}`}></i>
                                  </button>
                                )}
                                <button 
                                  type="button" 
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => resetarSenha(usuario.id!, usuario.nome)}
                                  title="Resetar senha"
                                >
                                  <i className="fas fa-key"></i>
                                </button>
                                {usuarioAtual && usuarioAtual.cargo === 'admin' && !!usuario.id && !!usuarioAtual.id && usuario.id !== String(usuarioAtual.id) && (
                                  <button 
                                    type="button" 
                                    className="btn btn-sm btn-danger"
                                    onClick={() => excluirUsuario(usuario.id!, usuario.nome)}
                                    title="Excluir usuário"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ListaUsuarios;
